import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Container, VStack, useToast } from '@chakra-ui/react';

import SearchToolbar from './components/SearchToolbar';
import ResultsPanel from './components/ResultsPanel';

import { getSuppliersListApi } from '../../../features/suppliers/suppliers.api';
import { searchInventoryApi } from '../../../features/inventories/inventories.api';
import { clearSearchResults } from '../../../features/inventories/inventories.slice';

const PAGE_SIZE = 20;

const InventorySearch = () => {
  const dispatch = useDispatch();
  const toast = useToast();

  const [searchQuery, setSearchQuery] = useState('');
  const [searchField, setSearchField] = useState('all');
  const [selectedSupplier, setSelectedSupplier] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState({});

  const { list: supplierOptions, getSuppliersListStatus } = useSelector(
    (state) => state.suppliers,
  );
  const {
    searchResults,
    searchTotalCount,
    searchTotalPages,
    searchCurrentPage,
    searchInventoryStatus,
  } = useSelector((state) => state.inventories);

  const isSearching = searchInventoryStatus === 'pending';

  useEffect(() => {
    dispatch(getSuppliersListApi());
  }, [dispatch]);

  const runSearch = useCallback(
    async ({
      page = 1,
      term = searchQuery,
      field = searchField,
      supplier = selectedSupplier,
    } = {}) => {
      const trimmed = String(term ?? '').trim();
      if (!trimmed) {
        toast({
          title: 'Search term required',
          description: 'Enter a SKU, partslink, OEM, or description to search.',
          status: 'warning',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      setHasSearched(true);
      setCurrentImageIndex({});

      try {
        await dispatch(
          searchInventoryApi({
            searchTerm: trimmed,
            searchField: field,
            supplierIds: supplier || undefined,
            page,
            limit: PAGE_SIZE,
            includeImages: false,
          }),
        ).unwrap();
      } catch (error) {
        toast({
          title: 'Search failed',
          description: error?.message || 'Could not search inventory.',
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
      }
    },
    [dispatch, searchQuery, searchField, selectedSupplier, toast],
  );

  const handleSearchFieldChange = (value) => {
    setSearchField(value);
    if (hasSearched && searchQuery.trim()) {
      runSearch({ page: 1, field: value });
    }
  };

  const handleSupplierChange = (value) => {
    setSelectedSupplier(value);
    if (searchQuery.trim()) {
      runSearch({ page: 1, supplier: value });
    } else {
      dispatch(clearSearchResults());
      setHasSearched(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      runSearch({ page: 1, term: e.target.value });
    }
  };

  const getProductId = (product, index) =>
    product.supplierIdentifier || product.oem || product.partslink || `product-${index}`;

  const nextImage = (productId) => {
    const productIndex = searchResults.findIndex(
      (p, idx) => getProductId(p, idx) === productId,
    );
    const product = searchResults[productIndex];
    if (!product?.images?.length) return;

    setCurrentImageIndex((prev) => ({
      ...prev,
      [productId]: ((prev[productId] || 0) + 1) % product.images.length,
    }));
  };

  const prevImage = (productId) => {
    const productIndex = searchResults.findIndex(
      (p, idx) => getProductId(p, idx) === productId,
    );
    const product = searchResults[productIndex];
    if (!product?.images?.length) return;

    setCurrentImageIndex((prev) => ({
      ...prev,
      [productId]:
        (prev[productId] || 0) === 0
          ? product.images.length - 1
          : (prev[productId] || 0) - 1,
    }));
  };

  const selectImage = (productId, index) => {
    setCurrentImageIndex((prev) => ({ ...prev, [productId]: index }));
  };

  return (
    <Box minH="100vh" pb={12}>
      <Container maxW="container.xl" py={8} mt="50px">
        <VStack spacing={6} align="stretch">
          <SearchToolbar
            searchQuery={searchQuery}
            searchField={searchField}
            selectedSupplier={selectedSupplier}
            supplierOptions={supplierOptions ?? []}
            suppliersLoading={getSuppliersListStatus === 'pending'}
            isSearching={isSearching}
            onSearchQueryChange={setSearchQuery}
            onSearchFieldChange={handleSearchFieldChange}
            onSupplierChange={handleSupplierChange}
            onSearch={() => runSearch({ page: 1 })}
            onKeyDown={handleKeyDown}
          />

          <ResultsPanel
            results={searchResults}
            totalCount={searchTotalCount}
            currentPage={searchCurrentPage}
            totalPages={searchTotalPages}
            isLoading={isSearching}
            hasSearched={hasSearched}
            searchField={searchField}
            imageIndexByProduct={currentImageIndex}
            onPageChange={(page) => runSearch({ page })}
            onPrevImage={prevImage}
            onNextImage={nextImage}
            onSelectImage={selectImage}
          />
        </VStack>
      </Container>
    </Box>
  );
};

export default InventorySearch;
